import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { Spectrum } from '@bangos/shared';
import { en } from './locales/en';
import { lt } from './locales/lt';

// ── Types ──

export type Locale = 'en' | 'lt';

// Flatten a nested object type into dot-notation string literal union.
// Works with readonly objects (as const).
type FlattenKeys<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string | ((...args: any[]) => string)
    ? `${P}${K}`
    : T[K] extends Record<string, unknown>
    ? FlattenKeys<T[K], `${P}${K}.`>
    : never;
}[keyof T & string];

export type TranslationKey = FlattenKeys<typeof en>;

// Locale dictionary shape: recursive string leaves
export type LocaleDict = {
  [K: string]: string | LocaleDict;
};

// Internal: concrete En type for the static map
type EnDict = typeof en;

// ── Static locale map ──

const locales: Record<Locale, EnDict> = { en, lt } as Record<Locale, EnDict>;

// ── Key resolution ──

function resolveKey(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

// ── Plural rules for Lithuanian ──

function ltPluralSuffix(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return ''; // singular: 1, 21, 31...
  if (mod10 >= 2 && mod10 <= 9 && !(mod100 >= 12 && mod100 <= 19)) return 's'; // plural: 2-9, 22-29
  return 'ų'; // genitive plural: 0, 10-20, 30...
}

function enPluralSuffix(count: number): string {
  return count !== 1 ? 's' : '';
}

// ── TFunction ──

type Params = Record<string, string | number>;

function interpolate(template: string, params?: Params, locale?: Locale): string {
  if (!params) return template;
  let result = template;

  // Handle special {plural} placeholder based on {remaining} or {count}
  if ('plural' in params === false) {
    // If template contains {plural} but params don't provide it,
    // try to derive it from {remaining} or {count}
    if (template.includes('{plural}')) {
      const count = typeof params.remaining === 'number' ? params.remaining
        : typeof params.count === 'number' ? params.count
        : 1;
      const suffix = locale === 'lt' ? ltPluralSuffix(count) : enPluralSuffix(count);
      result = result.replace(/\{plural\}/g, suffix);
    }
  }

  for (const [key, value] of Object.entries(params)) {
    if (key === 'plural') {
      result = result.replace(/\{plural\}/g, String(value));
    } else {
      result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value));
    }
  }
  return result;
}

// ── Rich text helper ──
export { RichText } from './RichText';

// ── Context ──

interface LocaleContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: TranslationKey, params?: Params) => string;
  formatNumber: (n: number) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

// ── Persistence helpers ──

const STORAGE_KEY = 'bangos_locale';
const EXPLICIT_KEY = 'bangos_locale_explicit';

function getInitialLocale(): Locale {
  try {
    const explicit = localStorage.getItem(EXPLICIT_KEY);
    if (explicit === '1') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'en' || stored === 'lt') return stored;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'lt') return stored;

    // Auto-detect from browser
    if (typeof navigator !== 'undefined' && navigator.language?.startsWith('lt')) {
      return 'lt';
    }
  } catch {
    // localStorage unavailable (e.g., Safari private browsing)
  }
  return 'en';
}

export function getInitialLocaleForSSR(): Locale {
  return 'en';
}

// ── Provider ──

interface LocaleProviderProps {
  children: ReactNode;
}

export function LocaleProvider({ children }: LocaleProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => getInitialLocale());

  // Sync html[lang] on mount
  useEffect(() => {
    if (locale === 'lt') {
      document.documentElement.lang = 'lt';
    } else {
      document.documentElement.lang = 'en';
    }
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
      localStorage.setItem(EXPLICIT_KEY, '1');
    } catch {
      // localStorage unavailable
    }
    document.documentElement.lang = l;
  }, []);

  const t = useCallback(
    (key: TranslationKey, params?: Params): string => {
      const dict = locales[locale];
      const raw = resolveKey(dict as unknown as Record<string, unknown>, key as string);
      if (typeof raw === 'function') {
        // For backward compat with any function-based values (none currently)
        return interpolate((raw as () => string)(), params, locale);
      }
      if (typeof raw === 'string') {
        return interpolate(raw, params, locale);
      }
      // Fallback: try English
      const enRaw = resolveKey(en as unknown as Record<string, unknown>, key as string);
      if (typeof enRaw === 'string') {
        return interpolate(enRaw, params, locale);
      }
      // Ultimate fallback: return the key itself
      return key as string;
    },
    [locale],
  );

  const formatNumber = useCallback(
    (n: number) => {
      return new Intl.NumberFormat(locale === 'lt' ? 'lt' : 'en').format(n);
    },
    [locale],
  );

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t, formatNumber }}>
      {children}
    </LocaleContext.Provider>
  );
}

// ── Hooks ──

export function useLocale(): [Locale, (l: Locale) => void] {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within a LocaleProvider');
  return [ctx.locale, ctx.setLocale];
}

export function useT(): (key: TranslationKey, params?: Params) => string {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useT must be used within a LocaleProvider');
  return ctx.t;
}

export function useNumberFormat(): (n: number) => string {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useNumberFormat must be used within a LocaleProvider');
  return ctx.formatNumber;
}

/**
 * Hook that returns a function to translate a Spectrum object's left/right labels
 * into the current locale. Falls back to English, then to the spectrum's raw strings
 * (which handles user-submitted spectra that have no translation).
 */
export function useTranslateSpectrum(): (spectrum: Spectrum) => { left: string; right: string } {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useTranslateSpectrum must be used within a LocaleProvider');
  const { locale } = ctx;

  return useCallback(
    (spectrum: Spectrum) => {
      const dict = locales[locale] as Record<string, unknown>;
      const spectraDict = dict.spectra as Record<string, { left: string; right: string }> | undefined;
      if (spectraDict?.[spectrum.id]) {
        return spectraDict[spectrum.id];
      }
      // Fallback to English
      const enSpectra = (en as unknown as Record<string, unknown>).spectra as
        | Record<string, { left: string; right: string }>
        | undefined;
      if (enSpectra?.[spectrum.id]) {
        return enSpectra[spectrum.id];
      }
      // Ultimate fallback: use the spectrum's own strings (handles custom/user-submitted spectra)
      return { left: spectrum.left, right: spectrum.right };
    },
    [locale],
  );
}
